import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Card, Col, Row, Alert, Badge, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import api from "../../../../api/axios";
import { decrypt } from "../../../../utils/crypto";
import config from "../../../../config";

const DocumentAcceptance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setError("Invalid or missing token");
      setLoading(false);
      return;
    }

    loadDocumentData(token);
  }, [searchParams]);

  const loadDocumentData = async (token) => {
    try {
      setLoading(true);
      const decryptedData = JSON.parse(decrypt(token));
      const { candidateId, companyId, documentId } = decryptedData;

      let docResponse, candidateResponse;
      docResponse = await api.get(
        `/api/v2/documents/${documentId}?company_id=${companyId}`
      );
      candidateResponse = await api.get(
        `/api/candidates/${candidateId}?company_id=${companyId}`
      );

      const documentData =
        docResponse.data?.result ||
        docResponse.data?.data ||
        docResponse.data?.document ||
        docResponse.data;
      const candidateData =
        candidateResponse.data?.result ||
        candidateResponse.data?.data ||
        candidateResponse.data?.candidate ||
        candidateResponse.data;

      if (!documentData) {
        throw new Error("Document data is empty");
      }
      if (!candidateData) {
        throw new Error("Candidate data is empty");
      }

      // Check if document is already signed
      if (documentData?.status === "SIGNED") {
        setError("This document has already been signed and accepted.");
        return;
      }

      setDocument(documentData);
      setCandidate(candidateData);
    } catch (error) {
      console.error("Error loading document data:", error);
      setError(
        error.message ||
          "Unable to load document information. The link may be invalid or expired."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptDocument = async () => {
    if (!document || !candidate) return;

    try {
      setAccepting(true);

      const token = searchParams.get("token");
      const decryptedData = JSON.parse(decrypt(token));
      const { companyId, documentId } = decryptedData;

      // Call the API to mark document as signed/accepted
      const response = await api.patch(
        `/api/v2/documents/${documentId}/mark-signed/${companyId}`,
        { notes: "signed via acceptance link" }
      );

      if (response.data?.success) {
        toast.success("Document accepted successfully!");
        navigate("/document-accepted", { replace: true });
      } else {
        throw new Error(response.data?.message || "Failed to accept document");
      }
    } catch (error) {
      console.error("Error accepting document:", error);

      if (error.response?.status === 400) {
        toast.error("Document has already been accepted or is no longer valid");
      } else if (error.response?.status === 404) {
        toast.error("Document not found");
      } else {
        toast.error(
          error.response?.data?.message ||
            "Failed to accept document. Please try again."
        );
      }
    } finally {
      setAccepting(false);
    }
  };
  const getAbsoluteResumeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${config?.IMAGE_URL || ""}${url}`;
  };
  const handlePreviewDocument = () => {
    const documentUrl =
      document?.url || document?.file_url || document?.document_url;
    const url = getAbsoluteResumeUrl(documentUrl);
    if (!url) {
      toast.info("Resume not available");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "60vh" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">Loading document information...</div>
        </div>
      </div>
    );
  }

  if (error) {
    const isAlreadySigned = error.includes("already been signed");

    return (
      <div className="container mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Alert
              variant={isAlreadySigned ? "info" : "danger"}
              className="text-center shadow-sm"
            >
              {isAlreadySigned ? (
                <>
                  <i className="fa fa-check-circle fa-5x mb-3 text-success"></i>
                  <h4 className="text-success">Document Already Accepted</h4>
                  <p className="mb-4">
                    This document has been successfully signed and accepted. No
                    further action is required.
                  </p>
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate("/")}
                  >
                    <i className="fa fa-home me-2"></i>
                    Go to Homepage
                  </Button>
                </>
              ) : (
                <>
                  <i className="fa fa-exclamation-triangle fa-3x mb-3"></i>
                  <h4>Error</h4>
                  <p>{error}</p>
                  <Button
                    variant="outline-danger"
                    onClick={() => navigate("/")}
                  >
                    Go to Homepage
                  </Button>
                </>
              )}
            </Alert>
          </Col>
        </Row>
      </div>
    );
  }

  if (!document || !candidate) {
    return (
      <div className="container mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Alert variant="warning" className="text-center">
              <h4>Document Not Found</h4>
              <p>
                The requested document could not be found or is no longer
                available.
              </p>
            </Alert>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">
                <i className="fa fa-file-text-o me-2"></i>
                Document Acceptance
              </h4>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Candidate Information */}
              <div className="mb-4">
                <h5 className="border-bottom pb-2">Candidate Information</h5>
                <Row>
                  <Col md={6}>
                    <strong>Name:</strong>{" "}
                    {candidate?.full_name ||
                      candidate?.name ||
                      candidate?.first_name + " " + candidate?.last_name ||
                      "N/A"}
                  </Col>
                  <Col md={6}>
                    <strong>Email:</strong> {candidate?.email || "N/A"}
                  </Col>
                </Row>
              </div>

              {/* Document Information */}
              <div className="mb-4">
                <h5 className="border-bottom pb-2">Document Information</h5>
                <Row>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Document Title:</strong>
                      <div>
                        {document?.title ||
                          document?.name ||
                          document?.document_name ||
                          "N/A"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Format:</strong>
                      <div>
                        <i className="fa fa-file-pdf-o text-danger me-1"></i>
                        {document?.format?.toUpperCase() ||
                          document?.file_format?.toUpperCase() ||
                          "PDF"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-2">
                      <strong>Created:</strong>
                      <div>
                        {document?.created_at
                          ? formatDate(document.created_at)
                          : document?.date_created
                          ? formatDate(document.date_created)
                          : "N/A"}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Document Preview */}
              <div className="mb-4">
                <h5 className="border-bottom pb-2">Document Preview</h5>
                <div className="text-center p-4 border rounded bg-light">
                  <i className="fa fa-file-pdf-o fa-4x text-danger mb-3"></i>
                  <div className="mb-3">
                    <strong>
                      {document?.title ||
                        document?.name ||
                        document?.document_name ||
                        "Document"}
                    </strong>
                  </div>
                  <Button
                    variant="outline-primary"
                    onClick={handlePreviewDocument}
                    disabled={
                      !document?.url &&
                      !document?.file_url &&
                      !document?.document_url
                    }
                  >
                    <i className="fa fa-eye me-2"></i>
                    Preview Document
                  </Button>
                </div>
              </div>

              {/* Acceptance Action */}
              <div className="text-center">
                <Alert variant="info" className="mb-4">
                  <i className="fa fa-info-circle me-2"></i>
                  By clicking "Accept Document" below, you acknowledge that you
                  have reviewed and accept the terms of this document.
                </Alert>

                <div className="d-flex justify-content-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/")}
                    disabled={accepting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="success"
                    onClick={handleAcceptDocument}
                    disabled={accepting}
                  >
                    {accepting ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-2" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-check me-2"></i>
                        Accept Document
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DocumentAcceptance;
